/**
 * customer_disposal_fees — #52 / DEC-358 (migration 0182). What a hauler CHARGES each
 * customer per bbl to dispose of its water at one place. Not hauler_disposal_rates
 * (disposal-pricing.ts), which is what the hauler PAYS that place. See the migration
 * header for the rules; the API reads and writes it with raw SQL, and this mirror keeps
 * schema-diff tooling aligned.
 *
 * - hauler_operator_id NULL is the place's DEFAULT fee; a customer row overrides it.
 * - Whole cents per bbl. fee_cents_per_bbl NULL ends a price from that day on.
 * - Append-only and dated: a change is a new row with a later effective_from.
 * - Targets EXACTLY ONE of a platform disposal facility or a dropoff-capable hauler
 *   location, mirroring hauler_disposal_rates.
 */

import { sql } from 'drizzle-orm';
import {
  check,
  date,
  index,
  integer,
  pgPolicy,
  pgTable,
  text,
  timestamp,
  uniqueIndex,
  uuid,
} from 'drizzle-orm/pg-core';
import { appUserRole } from './enums.js';
import { disposalFacilities } from './disposals.js';
import { haulerDisposalFacilities, haulerLocations, haulerOperators } from './platform-catalog.js';
import { tenants, users } from './users.js';

const DEFAULT_KEY = sql`COALESCE(hauler_operator_id, '00000000-0000-0000-0000-000000000000'::uuid)`;

export const customerDisposalFees = pgTable(
  'customer_disposal_fees',
  {
    id: uuid('id').primaryKey().defaultRandom(),
    tenantId: uuid('tenant_id')
      .notNull()
      .references(() => tenants.id, { onDelete: 'cascade' }),
    haulerOperatorId: uuid('hauler_operator_id').references(() => haulerOperators.id, {
      onDelete: 'cascade',
    }),
    disposalFacilityId: uuid('disposal_facility_id').references(() => disposalFacilities.id, {
      onDelete: 'restrict',
    }),
    dropoffHaulerLocationId: uuid('dropoff_hauler_location_id').references(
      () => haulerLocations.id,
      { onDelete: 'restrict' },
    ),
    // 0208 — or a catalog disposal well the hauler authorized (SWD destination A1).
    disposalWellId: uuid('disposal_well_id').references(() => haulerDisposalFacilities.id, {
      onDelete: 'restrict',
    }),
    feeCentsPerBbl: integer('fee_cents_per_bbl'),
    effectiveFrom: date('effective_from', { mode: 'string' }).notNull(),
    notes: text('notes'),
    importId: uuid('import_id'),
    createdBy: uuid('created_by').references(() => users.id, { onDelete: 'set null' }),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    check(
      'customer_disposal_fees_one_target',
      sql`num_nonnulls(disposal_facility_id, dropoff_hauler_location_id, disposal_well_id) = 1`,
    ),
    check(
      'customer_disposal_fees_fee_range',
      sql`fee_cents_per_bbl IS NULL OR fee_cents_per_bbl BETWEEN 0 AND 10000`,
    ),
    uniqueIndex('customer_disposal_fees_facility_day_uq')
      .on(t.tenantId, DEFAULT_KEY, t.disposalFacilityId, t.effectiveFrom)
      .where(sql`disposal_facility_id IS NOT NULL`),
    uniqueIndex('customer_disposal_fees_location_day_uq')
      .on(t.tenantId, DEFAULT_KEY, t.dropoffHaulerLocationId, t.effectiveFrom)
      .where(sql`dropoff_hauler_location_id IS NOT NULL`),
    uniqueIndex('customer_disposal_fees_well_day_uq')
      .on(t.tenantId, DEFAULT_KEY, t.disposalWellId, t.effectiveFrom)
      .where(sql`disposal_well_id IS NOT NULL`),
    index('customer_disposal_fees_customer_idx').on(t.tenantId, t.haulerOperatorId),
    pgPolicy('customer_disposal_fees_isolation', {
      as: 'permissive',
      to: appUserRole,
      for: 'all',
      using: sql`tenant_id = current_setting('app.current_tenant_id')::uuid`,
      withCheck: sql`tenant_id = current_setting('app.current_tenant_id')::uuid`,
    }),
  ],
);

export type CustomerDisposalFee = typeof customerDisposalFees.$inferSelect;
